import type { InternalMessageSchema, MessageHandler, PublicMessageSchema } from '@xpaid/core'
import type { Message, MessageSchema } from '..'
import type { SdkConfig } from './sdk.types'
import { InternalMessage, MessageBus, PublicMessage } from '@xpaid/core'

import { IframeManager } from '../shared/modules/iframe-manager'
import { getWebAppUrl } from '../shared/utils/get-web-app-url'

/**
 * XpaidWalletSdk - A robust, event-driven SDK for seamless iframe-based wallet interactions.
 */
export class XpaidWalletSdk {
  private static iframeManager: IframeManager | null = null
  private static messageBus: MessageBus<PublicMessageSchema & InternalMessageSchema> | null = null
  private static sdkConfig: SdkConfig
  private static messageQueue = new Map<Message, MessageSchema[Message]>()
  private static subscriptionsQueue = new Map<Message, MessageHandler<any>>()

  /**
   * Initializes the SDK, setting up the iframe and message listeners.
   * @param {SdkConfig} sdkConfig - The SDK configuration object.
   */
  public static initialize(sdkConfig: SdkConfig): void {
    if (this.isInitialized()) {
      this.log('SDK is already initialized.', 'warn')
      return
    }
    this.sdkConfig = sdkConfig

    const webAppUrl = getWebAppUrl(this.sdkConfig?.environment)

    this.iframeManager = new IframeManager({
      ...this.sdkConfig,
      src: webAppUrl,
    })

    this.iframeManager.init()
      .then((iframe) => {
        this.messageBus = new MessageBus({
          target: iframe.contentWindow!,
          allowedOrigins: [webAppUrl],
        })

        this.messageBus?.subscribe(PublicMessage.AppReady, () => {
          this.messageBus?.publish(InternalMessage.AppConfig, {
            hashToken: sdkConfig.hashToken,
            colorScheme: sdkConfig.colorScheme ?? {},
          })
        })

        this.messageQueue.forEach((payload, messageType) => {
          this.messageBus?.publish(messageType, payload)
        })

        this.subscriptionsQueue.forEach((handler, messageType) => {
          this.messageBus?.subscribe(messageType, handler)
        })
      })
  }

  /**
   * Cleans up the SDK, removing message listeners and destroying the iframe.
   */
  public static destroy(): void {
    if (!this.isInitialized()) {
      this.log('SDK is not initialized.', 'warn')
      return
    }

    this.messageBus?.destroy()
    this.iframeManager?.destroy()
    this.messageBus = null
    this.iframeManager = null
  }

  /**
   * Subscribes to wallet messages.
   * @param {PublicMessage} messageType - The message type.
   * @param {MessageHandler} handler - The callback function to execute on receiving the message.
   */
  public static subscribe<Message extends PublicMessage>(
    messageType: Message,
    handler: MessageHandler<PublicMessageSchema[Message]>,
  ): void {
    if (!this.messageBus) {
      this.subscriptionsQueue.set(messageType, handler)
      return
    }

    this.messageBus.subscribe(messageType, handler)
  }

  /**
   * Unsubscribes from wallet messages.
   * @param {PublicMessage} messageType - The message type to remove.
   * @param {MessageHandler} handler - The callback function to execute on receiving the message.
   */
  public static unsubscribe<Message extends PublicMessage>(messageType: Message, handler: MessageHandler<PublicMessageSchema[Message]>): void {
    if (!this.messageBus) {
      this.subscriptionsQueue.delete(messageType)
      return
    }

    this.messageBus.unsubscribe(messageType, handler)
  }

  public static publish<
    Message extends PublicMessage,
  >(
    messageType: Message,
    ...payload: (PublicMessageSchema & InternalMessageSchema)[Message] extends undefined
      ? []
      : [(PublicMessageSchema & InternalMessageSchema)[Message]]
  ): void {
    if (!this.messageBus) {
      if (payload.length > 0) {
        this.messageQueue.set(messageType, payload[0]!)
      }
      else {
        this.messageQueue.set(messageType, undefined)
      }
      return
    }

    if (payload.length > 0) {
      this.messageBus.publish(messageType, payload[0]!)
    }
    else {
      this.messageBus.publish(messageType)
    }
  }

  /**
   * Checks if the SDK is initialized.
   * @returns {boolean} - Whether the SDK is initialized.
   */
  private static isInitialized(): boolean {
    return this.iframeManager !== null && this.messageBus !== null
  }

  /**
   * Logs messages with different levels.
   * @param {string} message - The log message.
   * @param {'log' | 'warn' | 'error'} level - The log level.
   */
  private static log(message: string, level: 'log' | 'warn' | 'error' = 'log'): void {
    // eslint-disable-next-line no-console
    console[level](`[XpaidWalletSdk] => ${message}`)
  }
}
